import * as fs from "node:fs/promises";
import * as http from "node:http";
import * as os from "node:os";
import * as path from "node:path";
import cors from "cors";
import getPorts from './../_node_modules/get-port/index.js';
import * as h3 from "h3";
import fetch from './../_node_modules/node-fetch/src/index.js';
import * as z from "zod";
import { API_ENDPOINTS } from "../constants/API_ENDPOINTS.js";
import { PRISMIC_CLI_USER_AGENT } from "../constants/PRISMIC_CLI_USER_AGENT.js";
import { UnauthenticatedError, InternalError, UnexpectedDataError } from "../errors.js";
import { decode } from "../lib/decode.js";
import { createPrismicAuthManagerMiddleware } from "./createPrismicAuthManagerMiddleware.js";
const PERSISTED_AUTH_STATE_FILE_NAME = ".prismic";
const DEFAULT_PERSISTED_AUTH_STATE = {
  base: "https://prismic.io"
};
const PrismicAuthStateSchema = z.object({
  base: z.string(),
  token: z.string().optional()
});
const PrismicUserProfileSchema = z.object({
  userId: z.string(),
  shortId: z.string(),
  intercomHash: z.string(),
  email: z.string(),
  firstName: z.string(),
  lastName: z.string()
});
const checkHasAuthenticationToken = (authState) => {
  return Boolean(authState.token);
};
class PrismicAuthManager {
  scopedDirectory;
  constructor({ scopedDirectory = os.homedir() } = {}) {
    this.scopedDirectory = scopedDirectory;
  }
  async login(args) {
    const authState = await this._readPersistedAuthState();
    authState.base = API_ENDPOINTS.PrismicWroom;
    authState.token = args.token;
    await this._writePersistedAuthState(authState);
  }
  async getLoginSessionInfo() {
    const port = await getPorts({ port: 5555 });
    const url = new URL(`./dashboard/cli/login?source=prismic-cli&port=${port}`, API_ENDPOINTS.PrismicWroom).toString();
    return {
      port,
      url
    };
  }
  async nodeLoginSession(args) {
    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        server.close();
        reject(new Error("Login timeout, server did not receive a response within a 3-minute delay"));
      }, 18e4);
      const app = h3.createApp();
      app.use(h3.fromNodeMiddleware(cors()));
      app.use(h3.fromNodeMiddleware(createPrismicAuthManagerMiddleware({
        prismicAuthManager: this,
        onLoginCallback() {
          clearTimeout(timeout);
          server.close();
          resolve();
        }
      })));
      const server = http.createServer(h3.toNodeListener(app));
      server.once("listening", () => {
        if (args.onListenCallback) {
          args.onListenCallback();
        }
      });
      server.listen(args.port, "127.0.0.1");
    });
  }
  async logout() {
    const authState = await this._readPersistedAuthState();
    authState.token = void 0;
    await this._writePersistedAuthState(authState);
  }
  async checkIsLoggedIn() {
    const authState = await this._readPersistedAuthState();
    if (checkHasAuthenticationToken(authState)) {
      const url = new URL("./validate", API_ENDPOINTS.PrismicLegacyAuthenticationApi);
      url.searchParams.set("token", authState.token);
      let res;
      try {
        res = await fetch(url.toString(), {
          headers: {
            "User-Agent": PRISMIC_CLI_USER_AGENT
          }
        });
      } catch {
      }
      if (!res || !res.ok) {
        await this.logout();
        return false;
      }
      return true;
    } else {
      return false;
    }
  }
  async getAuthenticationToken() {
    let isLoggedIn = await this.checkIsLoggedIn();
    if (isLoggedIn) {
      const authState = await this._readPersistedAuthState();
      if (authState.token) {
        return authState.token;
      }
    }
    try {
      await this.refreshAuthenticationToken();
      isLoggedIn = await this.checkIsLoggedIn();
      if (isLoggedIn) {
        const authState = await this._readPersistedAuthState();
        if (authState.token) {
          return authState.token;
        }
      }
    } catch {
    }
    throw new UnauthenticatedError();
  }
  async refreshAuthenticationToken() {
    const authState = await this._readPersistedAuthState();
    if (checkHasAuthenticationToken(authState)) {
      const url = new URL("./refreshtoken", API_ENDPOINTS.PrismicLegacyAuthenticationApi);
      url.searchParams.set("token", authState.token);
      const res = await fetch(url.toString(), {
        headers: {
          "User-Agent": PRISMIC_CLI_USER_AGENT
        }
      });
      const text = await res.text();
      if (res.ok) {
        authState.token = text;
        await this._writePersistedAuthState(authState);
      } else {
        throw new InternalError("Failed to refresh authentication token.", {
          cause: text
        });
      }
    } else {
      throw new UnauthenticatedError();
    }
  }
  async getProfile() {
    const authenticationToken = await this.getAuthenticationToken();
    return await this._getProfileForAuthenticationToken({
      authenticationToken
    });
  }
  async _getProfileForAuthenticationToken(args) {
    const url = new URL("./profile", API_ENDPOINTS.PrismicLegacyUserApi);
    const res = await fetch(url.toString(), {
      headers: {
        Authorization: `Bearer ${args.authenticationToken}`,
        "User-Agent": PRISMIC_CLI_USER_AGENT
      }
    });
    if (res.ok) {
      const json = await res.json();
      const { value: profile, error } = decode(PrismicUserProfileSchema, json);
      if (error) {
        throw new UnexpectedDataError("Received invalid data from the Prismic user service.");
      }
      return profile;
    } else {
      const text = await res.text();
      throw new InternalError("Failed to retrieve profile from the Prismic user service.", {
        cause: text
      });
    }
  }
  async _readPersistedAuthState() {
    const authStateFilePath = this._getPersistedAuthStateFilePath();
    let authStateFileContents = JSON.stringify({});
    let rawAuthState = {};
    try {
      authStateFileContents = await fs.readFile(authStateFilePath, "utf8");
      rawAuthState = JSON.parse(authStateFileContents);
    } catch {
      rawAuthState = DEFAULT_PERSISTED_AUTH_STATE;
      authStateFileContents = JSON.stringify(rawAuthState, null, "	");
      await fs.mkdir(path.dirname(authStateFilePath), { recursive: true });
      await fs.writeFile(authStateFilePath, authStateFileContents);
    }
    const { value: authState, error } = decode(PrismicAuthStateSchema, rawAuthState);
    if (error) {
      throw new UnexpectedDataError("Prismic authentication state is invalid.");
    }
    return authState;
  }
  async _writePersistedAuthState(authState) {
    const authStateFilePath = this._getPersistedAuthStateFilePath();
    try {
      await fs.writeFile(authStateFilePath, JSON.stringify(authState, null, 2));
    } catch (error) {
      throw new InternalError("Failed to write Prismic authentication state to the file system.", {
        cause: error
      });
    }
  }
  _getPersistedAuthStateFilePath() {
    return path.resolve(this.scopedDirectory, PERSISTED_AUTH_STATE_FILE_NAME);
  }
}
export {
  PrismicAuthManager
};
//# sourceMappingURL=PrismicAuthManager.js.map
